import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Card, Typography } from "@mhome/ui";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import { listIntegrations } from "../service/integration-service";
import { useToast } from "../common/toast";
import { useWebSocket } from "../ws/websocket";
import IntegrationsFilter from "./integrations-filter";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PhonelinkSetupTwoToneIcon from "@mui/icons-material/PhonelinkSetupTwoTone";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import DeviceHubTwoToneIcon from "@mui/icons-material/DeviceHubTwoTone";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import NfcIcon from "@mui/icons-material/Nfc";
import { Bot } from "lucide-react";
import ResponsiveCardGrid from "../common/responsive-card-grid";
import { useIsSmallScreen } from "../common/adaptive-theme-provider";

const IntegrationListView = ({ showFilter = false, setShowFilter }) => {
  const { sendAndWaitWs } = useWebSocket();
  const { successToast, errorToast } = useToast();
  const [integrations, setIntegrations] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [sortBy, setSortBy] = useState("alphabet");
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const isSmallScreen = useIsSmallScreen();
  const wsReady = useRecoilValue(wsReadyState);
  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  useEffect(() => {
    if (wsReady) {
      setShowFilter(false);
      setFilterText("");
      handleLoadIntegrations();
    }
  }, [wsReady]);

  const handleLoadIntegrations = async () => {
    setLoading(true);
    const resp = await listIntegrations(sendAndWaitWs);

    if (resp) {
      setIntegrations(resp.integrations);
    } else {
      errorToast("Error loading integrations!");
    }
    setLoading(false);
  };

  const handleCardClick = (integration) => {
    const integrationId = integration.integration;
    history.push(`/integration?id=${integrationId}`);
  };

  const sortedIntegrations = integrations
    .filter((a) =>
      a.integration.toLowerCase().includes(filterText.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === "alphabet") {
        const aName = a.integration.toLowerCase();
        const bName = b.integration.toLowerCase();

        const aIsLetter = /^[a-zA-Z]/.test(aName);
        const bIsLetter = /^[a-zA-Z]/.test(bName);
        if (aIsLetter && !bIsLetter) return -1;
        if (!aIsLetter && bIsLetter) return 1;
        return aName.localeCompare(bName); //sort alphabetically
      }
    });

  const getLogo = (integration) => {
    const commonIconStyle = {
      width: "28px", // Using fixed pixel values instead of percentages
      height: "28px", // This ensures consistent size across all icons
    };

    switch (integration) {
      case "basic.automation":
        return <AutoModeIcon style={commonIconStyle} />;
      case "basic.ui":
        return <DashboardIcon style={commonIconStyle} />;
      case "basic.chat":
        return (
          <div
            className="flex items-center"
            style={{
              width: "28px",
              height: "28px",
            }}
          >
            <Bot style={commonIconStyle} />
          </div>
        );
      case "basic.device":
        return <LightbulbOutlinedIcon style={commonIconStyle} />;
      case "basic.phone":
        return <PhonelinkSetupTwoToneIcon style={commonIconStyle} />;
      case "basic.time":
        return <ScheduleIcon style={commonIconStyle} />;
      case "basic.weather":
        return <WbSunnyOutlinedIcon style={commonIconStyle} />;
      case "basic.notification":
        return <NotificationsOutlinedIcon style={commonIconStyle} />;
      case "basic.widget":
        return <DashboardIcon style={commonIconStyle} />;
      case "basic.nfc":
        return <NfcIcon style={commonIconStyle} />;
      default:
        return <DeviceHubTwoToneIcon style={commonIconStyle} />;
    }
  };

  return (
    <>
      {loading ? (
        <></>
      ) : (
        <div className="h-full" style={{ overflowX: "hidden", width: "100%" }}>
          <div
            className="p-4"
            style={{
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              paddingLeft: "8px",
              paddingRight: "8px",
            }}
          >
            {showFilter && (
              <IntegrationsFilter
                sortBy={sortBy}
                setSortBy={setSortBy}
                setFilterText={setFilterText}
                setShowFilter={setShowFilter}
              />
            )}
            <ResponsiveCardGrid
              breakpoints={{ xs: 2, sm: 3, md: 4, lg: 6 }}
              spacing={{ xs: 1, sm: 1.5, md: 2 }}
              style={{ width: "100%", boxSizing: "border-box", margin: 0 }}
            >
              {sortedIntegrations.map((integration) => (
                <Card
                  key={integration.integration}
                  elevation={0}
                  className="h-full p-4 cursor-pointer transition-all duration-200"
                  style={{
                    backgroundColor: integration.installed
                      ? getComponentBgColor("hsl(var(--card))")
                      : getComponentBgColor("hsl(var(--muted))"),
                    WebkitTapHighlightColor: "transparent",
                    boxShadow: "none",
                  }}
                  onClick={() => handleCardClick(integration)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.backgroundColor =
                      getComponentBgColor("hsl(var(--card))");
                  }}
                >
                  <div className="flex flex-row items-center h-full gap-3">
                    <div className="flex items-center justify-center flex-shrink-0">
                      {getLogo(integration.integration)}
                    </div>
                    <div className="flex flex-col items-start flex-grow min-w-0">
                      <div className="w-full flex items-center mb-1">
                        <Typography
                          variant="subtitle2"
                          className="font-medium flex-grow overflow-hidden text-ellipsis whitespace-nowrap"
                        >
                          {integration.name}
                        </Typography>
                        {integration.installed && (
                          <div className="ml-1 flex-shrink-0">
                            <DownloadDoneIcon
                              color="success"
                              style={{ fontSize: 16 }}
                            />
                          </div>
                        )}
                      </div>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        className="text-[0.7rem] w-full overflow-hidden text-ellipsis whitespace-nowrap"
                      >
                        {integration.org}
                      </Typography>
                    </div>
                  </div>
                </Card>
              ))}
            </ResponsiveCardGrid>
          </div>
        </div>
      )}
    </>
  );
};

export default IntegrationListView;
